import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, enderecoDeArquivo } from '@/shared/api/client';
import {
  documents,
  quoteFinancial,
  type OperationDetail,
  type QuoteFinancial,
  type QuoteInstallment,
  type QuoteInstallmentInput,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatMoney,
  moneyInputError,
  moneyToInput,
  parseMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { motivoDaCotacaoEncerrada } from '../portfolio/boletoAnexado';
import { fieldErrorOf } from '../shared/fieldError';
import { FinancialPanels } from './FinancialPanels';
import {
  chaveDoFinanceiro,
  formaDePagamento,
  nomeDoResumo,
  salvarArquivoDoFinanceiro,
  useFinanceiroDaCotacao,
  usePermissoesDoFinanceiro,
} from './financeiroDaCotacao';

/**
 * Financeiro da cotacao (P-COT-4), no Resumo da cotacao: as parcelas e os boletos, o
 * calculo financeiro (indisponivel com as regras pendentes) e o resumo em PDF. Ver
 * docs/ADR/0051-financeiro-da-cotacao.md.
 *
 * Nada aqui calcula. Numero, valor, juros e vencimento das parcelas sao os informados
 * por quem tem a alcada; a soma aparece ao lado do premio so para conferencia (PAR-002).
 * O boleto segue o modo da apolice: desligado, ou o boleto emitido fora e anexado.
 */
export function InstallmentsSection({ operacao }: { operacao: OperationDetail }) {
  const toast = useToast();
  const financeiro = useFinanceiroDaCotacao(operacao.id);
  const permissoes = usePermissoesDoFinanceiro(operacao);
  const [editando, setEditando] = useState(false);
  const [boletoDe, setBoletoDe] = useState<QuoteInstallment | null>(null);

  const baixarResumo = useMutation({
    mutationFn: () => quoteFinancial.summary(operacao.id),
    onSuccess: (arquivo) =>
      salvarArquivoDoFinanceiro(arquivo, nomeDoResumo(operacao, arquivo)),
    onError: (error: Error) => toast.error(error.message),
  });

  const baixarBoleto = useMutation({
    mutationFn: (numero: number) => quoteFinancial.billet(operacao.id, numero),
    onSuccess: (boleto) => {
      if (!boleto.available) {
        toast.error(boleto.reason);
        return;
      }
      window.open(enderecoDeArquivo(boleto.url), '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (financeiro.isPending) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-ink-500">Carregando o financeiro da cotacao.</p>
        </CardBody>
      </Card>
    );
  }
  if (financeiro.isError) {
    return (
      <Alert tone="danger" title="Financeiro da cotacao">
        Nao foi possivel carregar o financeiro da cotacao.
      </Alert>
    );
  }

  const dados = financeiro.data;
  const parcelas = dados.installments;
  const calculo = dados.calculation;
  const podeInformar = permissoes.financeiro && parcelas.editable;
  const anexado = parcelas.billet_mode === 'anexo';
  // Na cotacao encerrada o boleto da cotacao nao e entregue: depois da emissao ele e o
  // da apolice, e a cotacao recusada ou cancelada nao tem premio a pagar (R5-A08).
  const encerrada = motivoDaCotacaoEncerrada(parcelas.stage);

  return (
    <>
      <Card>
        <CardHeader
          title="Financeiro da cotacao"
          description="Parcelas, boletos e o calculo financeiro. Os valores sao os informados por quem tem a alcada: o calculo ainda depende de regras nao homologadas."
          action={
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!calculo.available}
                title={calculo.message}
              >
                Recalcular financeiro
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={baixarResumo.isPending}
                onClick={() => baixarResumo.mutate()}
              >
                Baixar resumo
              </Button>
            </div>
          }
        />
        <CardBody className="flex flex-col gap-4">
          {!calculo.available ? (
            <Alert tone="info" title="Calculo financeiro indisponivel">
              {calculo.message} O Atualizar cotacao grava sem recalcular ate a
              homologacao. Regras pendentes: {calculo.pending_rules.join(', ')}.
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <Dado
              rotulo="Numero de parcelas"
              valor={
                parcelas.installments_count === null
                  ? 'Nao informado'
                  : String(parcelas.installments_count)
              }
            />
            {/* A forma escolhida pela corretora no assistente (P-NCT-L). */}
            <Dado rotulo="Forma de pagamento" valor={formaDePagamento(dados)} />
            <Dado
              rotulo="Parcelamento bloqueado"
              valor={parcelas.locked ? 'Sim (guardado, sem efeito)' : 'Nao'}
            />
            <Dado
              rotulo="Soma das parcelas informadas"
              valor={formatMoney(parcelas.total)}
            />
            <Dado rotulo="Premio" valor={formatMoney(dados.premium)} />
            <Dado
              rotulo="Premio minimo informado"
              valor={
                dados.min_premium
                  ? `${formatMoney(dados.min_premium)}${dados.premium_is_minimum ? ' (premio igual ao minimo)' : ''}`
                  : 'Nao informado'
              }
            />
            <Dado
              rotulo="Primeira parcela"
              valor={
                parcelas.first_installment
                  ? `${formatMoney(parcelas.first_installment.amount)} em ${formatCivilDate(parcelas.first_installment.due_on)}`
                  : 'Sem parcelas'
              }
            />
          </div>

          {parcelas.items.length === 0 ? (
            <p className="text-sm text-ink-500">
              Nenhuma parcela informada. Sem parcelas na cotacao, a apolice recebe as
              parcelas informadas no detalhe dela.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-line text-2xs uppercase tracking-wide text-ink-500">
                    <th className="py-2 pr-3">Parcela</th>
                    <th className="py-2 pr-3">Vencimento</th>
                    <th className="py-2 pr-3">Valor</th>
                    <th className="py-2 pr-3">Juros</th>
                    <th className="py-2 pr-3">Boleto</th>
                  </tr>
                </thead>
                <tbody>
                  {parcelas.items.map((parcela) => (
                    <tr key={parcela.id} className="border-b border-line/60">
                      <td className="py-2 pr-3">
                        {String(parcela.number).padStart(3, '0')}
                      </td>
                      <td className="py-2 pr-3">{formatCivilDate(parcela.due_on)}</td>
                      <td className="py-2 pr-3">{formatMoney(parcela.amount)}</td>
                      <td className="py-2 pr-3">
                        {parcela.interest.amount === 0
                          ? 's/ juros'
                          : formatMoney(parcela.interest)}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!anexado || !parcela.billet || encerrada !== null}
                            title={
                              !anexado
                                ? 'A geracao de boleto esta desligada nesta empresa.'
                                : (encerrada ?? undefined)
                            }
                            onClick={() => baixarBoleto.mutate(parcela.number)}
                          >
                            Boleto
                          </Button>
                          {anexado &&
                          permissoes.financeiro &&
                          dados.issuance_editable ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setBoletoDe(parcela)}
                            >
                              {parcela.billet ? 'Trocar boleto' : 'Anexar boleto'}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {parcelas.billet_mode === 'desligado' ? (
            <p className="text-xs text-ink-500">
              A geracao de boleto esta desligada nesta empresa: nao ha integracao
              bancaria. A configuracao do boleto e a da apolice.
            </p>
          ) : null}

          {podeInformar ? (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setEditando(true)}>
                Informar parcelas
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <FinancialPanels operacao={operacao} financeiro={dados} />

      {editando ? (
        <InstallmentsDialog
          operacao={operacao}
          financeiro={dados}
          onClose={() => setEditando(false)}
        />
      ) : null}
      {boletoDe ? (
        <InstallmentBilletDialog
          operacao={operacao}
          parcela={boletoDe}
          onClose={() => setBoletoDe(null)}
        />
      ) : null}
    </>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-0.5 text-sm font-medium text-ink-900">{valor}</p>
    </div>
  );
}

interface LinhaDaParcela {
  chave: string;
  numero: string;
  vencimento: string;
  valor: string;
  juros: string;
  boleto: string;
}

function linhaDe(parcela: QuoteInstallment): LinhaDaParcela {
  return {
    chave: parcela.id,
    numero: String(parcela.number),
    vencimento: parcela.due_on,
    valor: moneyToInput(parcela.amount),
    juros: moneyToInput(parcela.interest),
    boleto: parcela.billet_number,
  };
}

/**
 * "Informar parcelas": a lista inteira, o numero de parcelas e o bloqueio. O numero e o
 * bloqueio sao dados guardados sem efeito (PAR-001; COT-196 sem fonte).
 */
function InstallmentsDialog({
  operacao,
  financeiro,
  onClose,
}: {
  operacao: OperationDetail;
  financeiro: QuoteFinancial;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [linhas, setLinhas] = useState<LinhaDaParcela[]>(() =>
    financeiro.installments.items.map(linhaDe),
  );
  const [quantidade, setQuantidade] = useState(
    financeiro.installments_count === null ? '' : String(financeiro.installments_count),
  );
  const [bloqueado, setBloqueado] = useState(financeiro.installments_locked);
  const [erroDoServidor, setErroDoServidor] = useState<ApiError | null>(null);

  const invalida = linhas.some(
    (linha) =>
      !/^\d+$/.test(linha.numero) ||
      linha.vencimento === '' ||
      parseMoney(linha.valor) === null ||
      moneyInputError(linha.juros) !== undefined,
  );
  const quantidadeInvalida = quantidade !== '' && !/^\d+$/.test(quantidade);

  const gravar = useMutation({
    mutationFn: async () => {
      const numero = quantidade === '' ? null : Number(quantidade);
      if (
        numero !== financeiro.installments_count ||
        bloqueado !== financeiro.installments_locked
      ) {
        await quoteFinancial.update(operacao.id, {
          installments_count: numero,
          installments_locked: bloqueado,
        });
      }
      const entradas: QuoteInstallmentInput[] = linhas.map((linha) => ({
        number: Number(linha.numero),
        amount: parseMoney(linha.valor)!,
        interest: linha.juros.trim() === '' ? null : parseMoney(linha.juros),
        due_on: linha.vencimento,
        billet_number: linha.boleto.trim(),
      }));
      return quoteFinancial.replaceInstallments(operacao.id, entradas);
    },
    onSuccess: () => {
      toast.success('Parcelas da cotacao gravadas.');
      onClose();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) setErroDoServidor(error);
      toast.error(error.message);
    },
    // A gravacao e feita em duas chamadas: se a segunda falha, a primeira ja gravou o
    // numero de parcelas. A tela le de novo o financeiro em qualquer caso (achado B16
    // da revisao das ondas 5 e 6).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: chaveDoFinanceiro(operacao.id) });
    },
  });

  const atualizar = (indice: number, campo: keyof LinhaDaParcela, valor: string) =>
    setLinhas(
      linhas.map((linha, i) => (i === indice ? { ...linha, [campo]: valor } : linha)),
    );

  return (
    <Modal
      open
      onClose={onClose}
      title="Informar parcelas"
      description="A lista inteira substitui a gravada. Nada divide o premio nem confere a soma: o parcelamento ainda e regra pendente."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={invalida || quantidadeInvalida}
            onClick={() => gravar.mutate()}
          >
            Gravar parcelas
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Numero de parcelas"
            inputMode="numeric"
            value={quantidade}
            error={
              quantidadeInvalida
                ? 'Use um numero inteiro.'
                : fieldErrorOf(erroDoServidor, 'installments_count')
            }
            onChange={(event) => setQuantidade(event.target.value.replace(/\D/g, ''))}
          />
          <label className="mt-6 flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={bloqueado}
              onChange={(event) => setBloqueado(event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Parcelamento bloqueado
          </label>
        </div>

        {linhas.map((linha, indice) => (
          <div key={linha.chave} className="grid items-end gap-3 sm:grid-cols-6">
            <Field
              label="Parcela"
              inputMode="numeric"
              value={linha.numero}
              error={fieldErrorOf(erroDoServidor, `installments[${indice}].number`)}
              onChange={(event) =>
                atualizar(indice, 'numero', event.target.value.replace(/\D/g, ''))
              }
            />
            <Field
              label="Vencimento"
              type="date"
              value={linha.vencimento}
              error={fieldErrorOf(erroDoServidor, `installments[${indice}].due_on`)}
              onChange={(event) => atualizar(indice, 'vencimento', event.target.value)}
            />
            <MoneyField
              label="Valor da parcela (R$)"
              value={linha.valor}
              error={
                moneyInputError(linha.valor) ??
                fieldErrorOf(erroDoServidor, `installments[${indice}].amount`)
              }
              onValueChange={(texto) => atualizar(indice, 'valor', texto)}
            />
            <MoneyField
              label="Juros da parcela (R$)"
              value={linha.juros}
              error={moneyInputError(linha.juros)}
              onValueChange={(texto) => atualizar(indice, 'juros', texto)}
            />
            <Field
              label="Numero do boleto"
              value={linha.boleto}
              maxLength={60}
              onChange={(event) => atualizar(indice, 'boleto', event.target.value)}
            />
            <Button
              variant="ghost"
              onClick={() => setLinhas(linhas.filter((_, i) => i !== indice))}
            >
              Tirar
            </Button>
          </div>
        ))}

        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={linhas.length >= 120}
            onClick={() =>
              setLinhas([
                ...linhas,
                {
                  chave: `nova-${Date.now()}-${linhas.length}`,
                  numero: String(
                    linhas.reduce(
                      (maior, l) => Math.max(maior, Number(l.numero) || 0),
                      0,
                    ) + 1,
                  ),
                  vencimento: '',
                  valor: '',
                  juros: '',
                  boleto: '',
                },
              ])
            }
          >
            Acrescentar parcela
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Anexar o boleto emitido fora do SeguraCore a parcela: um documento da operacao com
 * arquivo (ManualUploadIssuanceBillet do OnPoint). O mesmo documento em varias parcelas
 * e o boleto unico.
 */
function InstallmentBilletDialog({
  operacao,
  parcela,
  onClose,
}: {
  operacao: OperationDetail;
  parcela: QuoteInstallment;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [documento, setDocumento] = useState(parcela.billet?.document_id ?? '');
  const [numero, setNumero] = useState(parcela.billet_number);

  const lista = useQuery({
    queryKey: ['documents', { owner: operacao.id }],
    queryFn: ({ signal }) =>
      documents.list(
        { owner_type: 'operacao', owner_id: operacao.id, limit: 100 },
        signal,
      ),
  });
  const opcoes = (lista.data?.items ?? [])
    .filter((item) => item.current_version > 0)
    .map((item) => ({ value: item.id, label: item.title }));

  const gravar = useMutation({
    mutationFn: (documentId: string | null) =>
      quoteFinancial.attachBillet(operacao.id, parcela.number, {
        document_id: documentId,
        billet_number: numero.trim(),
      }),
    onSuccess: () => {
      toast.success('Boleto da parcela gravado.');
      void queryClient.invalidateQueries({ queryKey: chaveDoFinanceiro(operacao.id) });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Boleto da parcela ${String(parcela.number).padStart(3, '0')}`}
      description="O boleto e emitido fora do SeguraCore e anexado aqui como documento da operacao com arquivo."
      size="md"
      footer={
        <>
          {parcela.billet ? (
            <Button
              variant="danger"
              loading={gravar.isPending}
              onClick={() => gravar.mutate(null)}
            >
              Retirar boleto
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={documento === ''}
            onClick={() => gravar.mutate(documento)}
          >
            Anexar boleto
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Documento do boleto"
          placeholder={lista.isPending ? 'Carregando...' : 'Selecione o documento'}
          value={documento}
          options={opcoes}
          onChange={(event) => setDocumento(event.target.value)}
        />
        {!lista.isPending && opcoes.length === 0 ? (
          <Alert tone="info" title="Nenhum documento com arquivo">
            Envie o boleto na aba Documentos da operacao e volte para anexar.
          </Alert>
        ) : null}
        <Field
          label="Numero do boleto"
          value={numero}
          maxLength={60}
          onChange={(event) => setNumero(event.target.value)}
        />
      </div>
    </Modal>
  );
}
